import React, { useState } from "react";
import { SectionLabel } from "./ui";

const STEPS = [
  { n: "01", t: "Discover", d: "Week 1 — we map your highest-ROI workflow.", deliv: "Workflow + KPI scoreboard signed by your team." },
  { n: "02", t: "Design",   d: "Week 2–3 — we architect the agent, integrations and policies.", deliv: "Agent spec, tool list, guardrails, go/no-go gate." },
  { n: "03", t: "Deploy",   d: "Week 4–5 — we train on your data and integrate live.", deliv: "Agent running in shadow mode in your environment." },
  { n: "04", t: "Activate", d: "Week 6 — agent goes autonomous on a slice, then scales.", deliv: "Production · KPI dashboard live · ROI tracked weekly." },
];

export default function ProcessSection() {
  return (
    <section id="how" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <SectionLabel tone="accent">How we build</SectionLabel>
          <h2 className="mt-5 text-[36px] leading-[1.05] tracking-tight sm:text-[44px] lg:text-[52px]">
            <span className="text-white">Six weeks from kickoff</span>{" "}
            <span className="font-display italic grad-text">to first agent in production.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-white/65">
            No pilot purgatory. Fixed scope, fixed timeline, measurable outcomes — or we keep building free until you sign off.
          </p>
        </div>
        <div className="relative mt-14">
          <div aria-hidden="true" className="absolute left-6 right-6 top-6 hidden h-px bg-gradient-to-r from-cyan-300/20 via-blue-400/30 to-violet-400/20 lg:block" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            {STEPS.map(s => (
              <div key={s.n} className="relative rounded-2xl border border-white/[0.08] bg-[#070d24] p-6">
                <div className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-cyan-300 to-blue-500 text-[12px] font-mono font-semibold tracking-wider text-[#051030]">
                  {s.n}
                </div>
                <h3 className="mt-5 text-[20px] font-semibold tracking-tight text-white">{s.t}</h3>
                <p className="mt-2 text-[13.5px] text-white/65">{s.d}</p>
                <div className="mt-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-300/70">deliverable</div>
                  <div className="mt-1 text-[12.5px] text-white/75">{s.deliv}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-10 flex flex-col items-start gap-4 rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-amber-300/15 text-amber-200">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" /><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <div>
              <div className="text-[18px] font-semibold text-white">The EkkBaz AI Guarantee</div>
              <p className="mt-1 text-[14px] text-white/65 max-w-2xl">First agent live in production within 6 weeks, hitting agreed KPIs in 90 days — or you don&rsquo;t pay the remaining build fee. Written into every contract.</p>
            </div>
          </div>
          <a href="#book" className="shrink-0 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 px-5 py-2.5 text-[13.5px] font-semibold text-amber-100 hover:bg-amber-300/20">See terms →</a>
        </div>
      </div>
    </section>
  );
}

/* ============================== ROI ============================== */
export function RoiSection() {
  const [headcount, setHeadcount] = useState(40);
  const [costPerFTE, setCostPerFTE] = useState(35000);
  const [reps, setReps] = useState(60);
  const opsCost = headcount * costPerFTE;
  const opsSaved = opsCost * 0.42;
  const repsLift = reps * 18000;
  const yearOne = Math.round(opsSaved + repsLift);
  const yearOneFmt = "$" + yearOne.toLocaleString();
  const payback = yearOne ? Math.max(2, Math.round((140000 / yearOne) * 12)) : 12;
  const headcountReclaimed = Math.round(headcount * 0.42);

  return (
    <section className="relative bg-[#060c22] py-24 lg:py-32 border-y border-white/[0.05]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-start gap-10 lg:grid-cols-[1fr,1.2fr]">
          <div>
            <SectionLabel tone="amber">The math</SectionLabel>
            <h2 className="mt-5 text-[36px] leading-[1.05] tracking-tight sm:text-[42px] lg:text-[50px]">
              <span className="text-white">See what autonomous agents</span>{" "}
              <span className="font-display italic grad-text">are worth to you.</span>
            </h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-white/65 max-w-md">
              A back-of-envelope model based on what our customers see in their first 12 months. Move the sliders.
            </p>
            <div className="mt-8 space-y-6">
              <Slider label="Ops & support headcount in scope" value={headcount} setValue={setHeadcount} min={5} max={500} step={5} suffix=" people" />
              <Slider label="Fully-loaded cost per FTE / yr" value={costPerFTE} setValue={setCostPerFTE} min={8000} max={120000} step={1000} prefix="$" suffix=" / yr" />
              <Slider label="Revenue-generating reps in scope" value={reps} setValue={setReps} min={0} max={500} step={5} suffix=" reps" />
            </div>
            <p className="mt-6 text-[11px] text-white/40 font-mono">
              Conservative model · 42% ops reduction · +$18K GMV / rep / yr · setup amortized over 12mo. Your actual numbers come out of week-1 discovery.
            </p>
          </div>
          <div className="rounded-3xl border border-white/[0.08] bg-gradient-to-b from-[#0b1535] to-[#070d24] p-8 lg:p-10">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-300/80">Projected · year 1</div>
              <div className="font-mono text-[10px] text-white/40">model v3.1</div>
            </div>
            <div className="mt-3 text-[64px] font-semibold tracking-tight leading-none nums grad-text">{yearOneFmt}</div>
            <div className="mt-2 text-[14px] text-white/55">in operational savings + revenue lift</div>
            <div className="mt-8 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.06]">
              <KPI k="Payback period" v={`${payback} mo`} sub="vs industry: 18–24" />
              <KPI k="FTEs reclaimed" v={`${headcountReclaimed}`} sub="redeployed to growth" />
              <KPI k="Ops time saved" v="42%" sub="recurring work" />
            </div>
            <div className="mt-8 rounded-xl border border-white/[0.08] bg-white/[0.025] p-5">
              <div className="text-[13.5px] text-white/70">Want a precision model for your business?</div>
              <a href="#book" className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[14px] font-semibold text-[#051030] hover:-translate-y-0.5">
                Get a custom ROI brief
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Slider = ({ label, value, setValue, min, max, step, prefix = "", suffix = "" }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <label className="text-[13.5px] text-white/70">{label}</label>
      <div className="font-mono text-[14px] text-white nums">{prefix}{value.toLocaleString()}{suffix}</div>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={e => setValue(Number(e.target.value))} className="mt-2 w-full accent-cyan-300" />
  </div>
);

const KPI = ({ k, v, sub }) => (
  <div className="bg-[#070d24] p-5">
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{k}</div>
    <div className="mt-1 text-[26px] font-semibold tracking-tight nums text-white">{v}</div>
    <div className="font-mono text-[10.5px] text-white/40 mt-0.5">{sub}</div>
  </div>
);
